import { createApiClient, type AxiosInstance } from '@commerce-os/api-client';
import { useUiStore } from '../stores/uiStore';
import { useBillingGuardStore, type BillingGuardState } from '../stores/billingGuardStore';

/**
 * `VITE_API_BASE_URL` is baked into the bundle at build time (see the
 * `ARG VITE_API_BASE_URL` block in apps/dashboard/Dockerfile). In dev,
 * the default `/api/v1` routes through Vite's dev-server proxy. In
 * production on Render, the operator sets this to the API service's
 * absolute URL — typically `https://commerce-api-XXXX.onrender.com/api/v1`
 * or `https://api.<custom-domain>/api/v1` if a custom domain is mapped.
 *
 * `commerce-api`'s `CORS_ORIGINS` env var must include this dashboard's
 * origin (e.g. `https://app.getcommerceos.com`) for cross-origin
 * requests to succeed.
 */
export const API_BASE_URL =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api/v1';

let apiClient: AxiosInstance | null = null;

export interface TokenPair {
  accessToken: string;
  refreshToken: string;
}

function getTokens() {
  const raw = localStorage.getItem('auth-tokens');
  if (!raw) return null;
  try {
    return JSON.parse(raw) as TokenPair;
  } catch {
    return null;
  }
}

function setTokens(accessToken: string, refreshToken: string) {
  localStorage.setItem('auth-tokens', JSON.stringify({ accessToken, refreshToken }));
}

function clearTokens() {
  localStorage.removeItem('auth-tokens');
}

/**
 * `POST /auth/refresh` with the stored refresh token.
 *
 * Raw `fetch`, not the axios client: the client's 401 interceptor is what
 * calls this, and a refresh that itself 401'd would re-enter it. Sends
 * `{ locationId }` only when a location is given, so the re-issued access
 * token carries (or drops) the `loc` claim exactly as the caller intends.
 *
 * Resolves with the new pair and does NOT persist it — the two callers
 * persist under their own rules: the interceptor's `onUnauthorized` clears
 * the session and redirects on failure, and `authStore.switchLocation`
 * discards a pair that a newer switch has already superseded. Rejects on a
 * non-OK status or a body missing either token (persisting `undefined`
 * would leave a session that can never authenticate).
 */
/** Fired when a silent refresh returns tokens for a DIFFERENT org — a
 *  platform admin's support session ended or ran out. The shell listens
 *  and re-hydrates the user for the org they're now in. */
export const ORG_CHANGED_EVENT = 'commerceos:org-changed';

/** The `org` claim of an access token (unverified — only compared). */
export function orgClaim(accessToken: string | undefined | null): string | null {
  if (!accessToken) return null;
  try {
    const part = accessToken.split('.')[1];
    if (!part) return null;
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    const org = (JSON.parse(json) as { org?: unknown }).org;
    return typeof org === 'string' ? org : null;
  } catch {
    return null;
  }
}

export async function refreshTokens(locationId?: string): Promise<TokenPair> {
  const refreshToken = getTokens()?.refreshToken;
  if (!refreshToken) throw new Error('No refresh token');

  const res = await fetch(`${API_BASE_URL}/auth/refresh`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${refreshToken}`,
    },
    body: JSON.stringify(locationId ? { locationId } : {}),
  });

  if (!res.ok) throw new Error('Refresh failed');

  const data = (await res.json()) as Partial<TokenPair> | null;
  if (typeof data?.accessToken !== 'string' || typeof data.refreshToken !== 'string') {
    throw new Error('Refresh response did not include a token pair');
  }
  return { accessToken: data.accessToken, refreshToken: data.refreshToken };
}

/**
 * Public routes that render without a session. Background calls that fire on
 * mount from these pages — telemetry page-views, help tooltips/walkthroughs —
 * go through this same axios client, so on production they 401 (no token).
 * Without this guard, `onUnauthorized` below hard-redirects to `/login`, which
 * reloads the SPA, which re-fires the same unauthenticated call, which 401s
 * again: an infinite refresh loop that made the login / forgot-password /
 * reset-password pages unusable. Only redirect when a real session on a
 * PROTECTED page has expired — never when we're already on a public page.
 */
const PUBLIC_AUTH_PATHS = [
  '/login',
  '/forgot-password',
  '/reset-password',
  '/register',
  '/mfa',
  '/sso/callback',
];

/** Pure: is `path` one of the public auth routes (or a nested child of one,
 *  e.g. `/reset-password/:token`)? Extracted for direct unit testing. */
function isPublicAuthPath(path: string): boolean {
  return PUBLIC_AUTH_PATHS.some((p) => path === p || path.startsWith(`${p}/`));
}

function redirectToLogin(): void {
  if (typeof window === 'undefined') return;
  if (isPublicAuthPath(window.location.pathname)) return;
  window.location.href = '/login';
}

export function getApiClient(): AxiosInstance {
  if (apiClient) return apiClient;

  const client = createApiClient({
    baseURL: API_BASE_URL,
    getAccessToken: () => getTokens()?.accessToken ?? null,
    onUnauthorized: async () => {
      const tokens = getTokens();
      if (!tokens?.refreshToken) {
        clearTokens();
        redirectToLogin();
        throw new Error('No refresh token');
      }

      try {
        // Preserve the current location scope across silent refreshes
        // so the new access token still carries the loc claim.
        const locationId = useUiStore.getState().selectedLocation?.id;
        const previousOrg = orgClaim(tokens.accessToken);
        const data = await refreshTokens(locationId);
        setTokens(data.accessToken, data.refreshToken);
        // A platform admin's support session ended (or ran out) and the
        // refresh returned them to their home org: the app must re-hydrate
        // for it, or it keeps calling the merchant's org-scoped URLs.
        if (previousOrg && orgClaim(data.accessToken) !== previousOrg) {
          window.dispatchEvent(new CustomEvent(ORG_CHANGED_EVENT));
        }
      } catch {
        clearTokens();
        redirectToLogin();
        throw new Error('Token refresh failed');
      }
    },
  });

  /**
   * Phase 36.4 — `SubscriptionActiveGuard` 402 capture.
   *
   * The backend's APP_GUARD-mounted `SubscriptionActiveGuard` rejects writes
   * for past-grace / canceled subscriptions with a 402 Payment Required
   * carrying `{code, message, subscriptionStatus, gracePeriodEndsAt?, portalRoute?}`.
   * We push the body into `useBillingGuardStore` so the AppShell-mounted
   * `BillingGuardScreen` overlay renders the merchant-facing restore-billing
   * surface, then re-throw so the calling React Query mutation still
   * surfaces inline error state in whatever form it was already rendering.
   *
   * Defense-in-depth: we never overwrite the guard state for `/billing/*`
   * or `/auth/*` calls. GETs against billing should never 402 (the guard
   * exempts read methods server-side), and re-issuing the overlay during
   * an auth flow would trap the user in an infinite redirect loop.
   */
  client.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      if (status === 402) {
        const url: string = (error.config?.url ?? '').toString();
        const isBillingOrAuth = url.startsWith('/billing/') || url.startsWith('/auth/');
        if (!isBillingOrAuth) {
          const body = error.response?.data as Partial<BillingGuardState> | undefined;
          if (body && typeof body === 'object') {
            useBillingGuardStore.getState().setGuard({
              code: typeof body.code === 'string' ? body.code : 'SUBSCRIPTION_INACTIVE',
              message:
                typeof body.message === 'string'
                  ? body.message
                  : 'Your subscription needs attention before you can make changes.',
              subscriptionStatus:
                typeof body.subscriptionStatus === 'string'
                  ? body.subscriptionStatus
                  : 'unknown',
              gracePeriodEndsAt: body.gracePeriodEndsAt ?? null,
              portalRoute: body.portalRoute,
            });
          }
        }
      }
      return Promise.reject(error);
    },
  );

  apiClient = client;
  return apiClient;
}

export { setTokens, getTokens, clearTokens, isPublicAuthPath, PUBLIC_AUTH_PATHS };
